import {faSpinnerThird} from '@fortawesome/pro-light-svg-icons'
import {forwardRef} from 'react'

import {twMerge} from '@sdox/utils'
import {getTrackingProps} from '@sdox/utils/dom'

import {FaIcon} from '../FaIcon'
import styles from './button.module.scss'

import type {TrackingProps} from '@sdox/utils/dom'
import type {ComponentProps, ReactNode, Ref} from 'react'

export type ButtonVariant =
  'filled' |
  'filled-inversed' |
  'filled-brand' |
  'outline' |
  'outline-inversed' |
  'overlay' |
  'overlay-inversed' |
  'overlay-brand' |
  'flat'

export type ButtonSize = 'none' | 'xsmall' | 'small' | 'medium' | 'large'

export interface ButtonBaseProps {
  variant?: ButtonVariant
  size?: ButtonSize
  leading?: ReactNode
  trailing?: ReactNode
  loading?: boolean
  contentClassName?: string
  // some matomo attributes for internal tracking
  mboAttrs?: TrackingProps
}

export interface ButtonProps extends ComponentProps<'button'>, ButtonBaseProps {
  ref?: Ref<HTMLButtonElement>
}

const variantStyles: Record<NonNullable<ButtonProps['variant']>, string> = {
  'filled': styles.btnFilled,
  'filled-inversed': styles.btnFilledInversed,
  'filled-brand': styles.btnFilledBrand,
  'outline': styles.btnOutline,
  'outline-inversed': styles.btnOutlineInversed,
  'overlay': styles.btnOverlay,
  'overlay-inversed': styles.btnOverlayInversed,
  'overlay-brand': styles.btnOverlayBrand,
  'flat': styles.btnFlat,
}

const sizeStyles: Record<NonNullable<ButtonProps['size']>, string> = {
  none: '',
  xsmall: styles.btnExtraSmall,
  small: styles.btnSmall,
  medium: styles.btnMedium,
  large: styles.btnLarge,
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const {
    variant = 'filled',
    size = 'large',
    leading,
    trailing,
    loading = false,
    className,
    children,
    contentClassName,
    type = 'button',
    mboAttrs,
    ...restProps
  } = props

  let {disabled} = restProps
  disabled ||= loading

  const mergedClassName = twMerge(
    variantStyles[variant],
    sizeStyles[size],
    styles.btn,
    loading && styles.loading,
    className,
  )

  return (
    <button
      className={mergedClassName}
      ref={ref}
      disabled={disabled}
      type={type}
      {...(mboAttrs ? getTrackingProps(mboAttrs) : null)}
      {...restProps}
    >
      {loading && (
        <span
          className={'absolute inset-0 flex items-center justify-center'}
        >
          <FaIcon
            className={styles.spinner}
            icon={faSpinnerThird}
          />
        </span>
      )}
      <span className={styles.btnContentWrapper}>
        {leading}
        {children && (
          <span className={twMerge(styles.content, contentClassName)}>
            {children}
          </span>
        )}
        {trailing}
      </span>
    </button>
  )
},
)

Button.displayName = 'Button'
